import React, { useState, useEffect } from 'react';
import { RadioGroup, RadioItem } from "../components/ui/RadioGroup";
import { MultiSelect } from '../components/ui/MultiSelect';
import { Spinner } from '../components/ui/Spinner';
import { FormField } from '../components/FormField';
import { useUsersApi } from '../apis/users-apis';
import { useToast } from "../components/ui/Toast";
import { ChevronDown, ChevronRight, Info, ShieldCheck } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { EntityWithAccessControl, AccessControlConfig, AccessType, UserData } from '../types';


interface UserResponse {
  users: UserData[];
}

interface EntityWithAccessControlAndOwner {
  accessControl: AccessControlConfig;
  ownerId: string;
}

// Simple Tooltip Component
const Tooltip: React.FC<{
  content: string;
  children: React.ReactNode;
  className?: string;
}> = ({ content, children, className }) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        className={className}
      >
        {children}
      </div>
      {isVisible && (
        <div className="absolute z-50 w-72 p-2 mt-2 text-sm text-white bg-gray-900 rounded-lg shadow-lg -translate-x-1/2 left-1/2">
          <div className="flex items-start gap-2">
            <Info className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <p>{content}</p>
          </div>
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 border-4 border-transparent border-b-gray-900" />
        </div>
      )}
    </div>
  );
};

const RadioOptionWithTooltip: React.FC<{
  option: AccessType;
  currentAccessType: AccessType;
  isDisabled: boolean;
  accessLevel: 'view' | 'edit';
  entityType: string;
  disabledMessage?: string;
  onValueChange: (value: AccessType) => void;
}> = ({
  option,
  currentAccessType,
  isDisabled,
  accessLevel,
  entityType,
  disabledMessage,
  onValueChange
}) => {
  const getDescription = (option: AccessType, accessLevel: 'view' | 'edit') => {
    switch (option) {
      case 'Owner Only':
        return accessLevel === 'view' 
          ? `Only the owner can view this ${entityType}`
          : `Only the owner can make changes to this ${entityType}`;
      case 'Allowed Users':
        return accessLevel === 'view'
          ? `Select specific users who can view this ${entityType}`
          : `Select specific users who can modify this ${entityType}`;
      case 'All Users':
        return accessLevel === 'view'
          ? `All users can view this ${entityType}`
          : `All users can make changes to this ${entityType}`;
    }
  };

  const content = (
    <div 
      className={cn(
        "bg-gray-50 p-4 rounded-lg border transition-colors",
        isDisabled 
          ? "border-gray-200 opacity-50 cursor-not-allowed" 
          : "border-gray-200 hover:border-gray-300"
      )}
    >
      <RadioGroup 
        value={currentAccessType} 
        onValueChange={(value) => !isDisabled && onValueChange(value as AccessType)}
      >
        <RadioItem 
          value={option}
          id={`${accessLevel}-${option.toLowerCase().replace(' ', '-')}`}
          label={option}
          description={getDescription(option, accessLevel)}
          disabled={isDisabled}
        />
      </RadioGroup>
    </div>
  );

  return isDisabled && disabledMessage ? (
    <Tooltip content={disabledMessage}>
      {content}
    </Tooltip>
  ) : content;
};

interface CollapsibleSectionProps {
  title: string;
  description?: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
  icon?: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  children,
  defaultOpen = false,
  icon
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border rounded-lg bg-white shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {icon && <div className="text-gray-500">{icon}</div>}
          <div className="text-left">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              {title}
            </h3>
            {description && (
              <p className="text-sm text-gray-500 mt-1">{description}</p>
            )}
          </div>
        </div>
        <div className="text-gray-400">
          {isOpen ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </div>
      </button>
      {isOpen && <div className="px-6 pb-6">{children}</div>}
    </div>
  );
};

export const useUsersData = () => {
  const [users, setUsers] = useState<UserData[]>([]);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const { addToast } = useToast();
  const { getUsers } = useUsersApi();

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoadingUsers(true);
      try {
        const data = await getUsers();
        const activeUsers = data.filter(user => user.isActive);
        setUsers(activeUsers);
      } catch (error) {
        console.error('Error fetching users:', error);
        addToast({
          title: "Error",
          description: "Failed to fetch users list",
          variant: "destructive",
          duration: 4000
        });
      } finally {
        setIsLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [addToast]);

  return { users, isLoadingUsers };
};

// Generic AccessControlSection props
interface AccessControlSectionProps<T extends EntityWithAccessControl> {
  entity: T;
  entityType: string; // e.g., 'skill', 'agent', 'package'
  handleInputChange: (field: keyof T, value: any) => void;
  errors: Record<string, string>;
}

export const AccessControlSection = <T extends EntityWithAccessControlAndOwner>({
  entity,
  entityType,
  handleInputChange,
  errors
}: AccessControlSectionProps<T>) => {
  const { users, isLoadingUsers } = useUsersData();
  const { user } = useAuth();
  const { addToast } = useToast();
  const { getUsers } = useUsersApi();

  const [isCurrentUserAdmin, setIsCurrentUserAdmin] = useState(false);
  const [accessConfig, setAccessConfig] = useState<AccessControlConfig>({
    viewAccessType: 'Owner Only',
    viewAllowedUsers: [],
    editAccessType: 'Owner Only',
    editAllowedUsers: []
  });

  // Fetch admin status when component mounts
  useEffect(() => {
    const fetchAdminStatus = async () => {
      if (user?.sub) {
        try {
          const users = await getUsers();
          const userData = users.find(u => u.userId === user.sub);
          setIsCurrentUserAdmin(userData?.isAdmin || false);
        } catch (error) {
          console.error('Error fetching user data:', error);
          setIsCurrentUserAdmin(false);
        }
      }
    };

    fetchAdminStatus();
  }, [user?.sub]);

  const isOwner = React.useMemo(() => {
    // For new entities, the current user should be considered the owner
    const isNewEntity = !entity.ownerId;
    return isNewEntity || entity.ownerId === user?.sub;
  }, [user?.sub, entity.ownerId]);

  // Check if current user has admin-level edit access
  const hasAdminAccess = React.useMemo(() => {
    return (
      isOwner || 
      (accessConfig.editAccessType === 'All Users') ||
      (accessConfig.editAccessType === 'Allowed Users' && 
       accessConfig.editAllowedUsers.includes(user?.sub || ''))
    );
  }, [accessConfig, user?.sub, isOwner]);

  // Check if user has edit access
  const hasEditAccess = React.useMemo(() => {
    if (!user?.sub) return false;
    if (isOwner || isCurrentUserAdmin) return true;
    
    switch (accessConfig.editAccessType) {
      case 'All Users':
        return true;
      case 'Allowed Users':
        return accessConfig.editAllowedUsers.includes(user.sub);
      case 'Owner Only':
        return false;
      default:
        return false;
    }
  }, [accessConfig, user?.sub, isOwner, isCurrentUserAdmin]);

  useEffect(() => {
    if (entity.accessControl) {
      setAccessConfig(entity.accessControl);
    }
  }, [entity.accessControl]);

  const updateAccessControl = (updates: Partial<AccessControlConfig>) => {
    const newConfig: AccessControlConfig = {
      ...accessConfig,
      ...updates
    };
    setAccessConfig(newConfig);
    handleInputChange('accessControl' as keyof T, newConfig);
  };

  // Helper function to enforce logical constraints
  const enforceAccessLogic = (
    level: 'view' | 'edit',
    newType: string,
    newUsers: string[] = []
  ): Partial<AccessControlConfig> => {
    const updates: Partial<AccessControlConfig> = {};

    if (level === 'view') {
      updates.viewAccessType = newType as AccessType;
      updates.viewAllowedUsers = newUsers;

      if (newType === 'Owner Only') {
        updates.editAccessType = 'Owner Only';
        updates.editAllowedUsers = [];
      }
      // Rule 2: If View Access = Allowed Users, restrict Edit Access options
      else if (newType === 'Allowed Users') {
        // If current edit access is All Users or has users not in view access,
        // reset it to Owner Only
        if (accessConfig.editAccessType === 'All Users' ||
            (accessConfig.editAccessType === 'Allowed Users' &&
             accessConfig.editAllowedUsers.some(user => !newUsers.includes(user)))) {
          updates.editAccessType = 'Owner Only';
          updates.editAllowedUsers = [];
        }
      }
      // Rule 3: View Access = All Users keeps Edit Access options open
    }
    else if (level === 'edit') {
      updates.editAccessType = newType as AccessType;
      updates.editAllowedUsers = newUsers;

      // Rule 5: If Edit Access = Allowed Users, those users must have View Access
      if (newType === 'Allowed Users') {
        if (accessConfig.viewAccessType === 'Owner Only') {
          updates.viewAccessType = 'Allowed Users';
          updates.viewAllowedUsers = newUsers;
        } else if (accessConfig.viewAccessType === 'Allowed Users') {
          // Add edit users to view users if they're not already included
          const combinedViewUsers = Array.from(new Set([
            ...accessConfig.viewAllowedUsers,
            ...newUsers
          ]));
          updates.viewAllowedUsers = combinedViewUsers;
        }
      }
      // Rule 6: If Edit Access = All Users, View Access must be All Users
      else if (newType === 'All Users') {
        updates.viewAccessType = 'All Users';
        updates.viewAllowedUsers = [];
      }
    }

    return updates;
  };

  const handleAccessTypeChange = (accessLevel: 'view' | 'edit', value: string) => {
    // For new entities or if user is owner/admin, allow access changes
    if (!isOwner && !isCurrentUserAdmin && entity.ownerId) {
      addToast({
        title: "Permission Denied",
        description: "Only owners and administrators can change access levels",
        variant: "destructive"
      });
      return;
    }
    
    const updates = enforceAccessLogic(accessLevel, value);
    updateAccessControl(updates);
  };

  const handleAllowedUsersChange = (accessLevel: 'view' | 'edit', newUsers: string[]) => {
    if (!hasEditAccess) {
      addToast({
        title: "Permission Denied",
        description: "You don't have permission to modify user access",
        variant: "destructive"
      });
      return;
    }

    const currentUsers = accessLevel === 'view' 
      ? accessConfig.viewAllowedUsers 
      : accessConfig.editAllowedUsers;

    // Determine which users are being added and which are being removed
    const addedUsers = newUsers.filter(u => !currentUsers.includes(u));
    const removedUsers = currentUsers.filter(u => !newUsers.includes(u));

    // If users are being removed and user is not owner/admin, prevent the change
    if (removedUsers.length > 0 && !isOwner && !isCurrentUserAdmin) {
      addToast({
        title: "Permission Denied",
        description: "Only owners and administrators can remove users from the access list",
        variant: "destructive"
      });
      return;
    }

    const updates = enforceAccessLogic(
      accessLevel,
      accessLevel === 'view' ? accessConfig.viewAccessType : accessConfig.editAccessType,
      newUsers
    );
    updateAccessControl(updates);
  };

  const isEditOptionDisabled = (option: string, currentViewAccess: string, viewAllowedUsers: string[]) => {
    // Rule 1: If View Access is Owner Only, only Owner Only is available for Edit
    if (currentViewAccess === 'Owner Only') {
      return option !== 'Owner Only';
    }
    // Rule 2: If View Access is Allowed Users, can't set Edit to All Users
    if (currentViewAccess === 'Allowed Users') {
      return option === 'All Users';
    }
    return false; // No restrictions if View Access is All Users
  };

  const getDisabledMessage = (option: string, currentViewAccess: string): string | undefined => {
    if (currentViewAccess === 'Owner Only') {
      return "When View Access is set to 'Owner Only', Edit Access must also be 'Owner Only' to maintain security";
    }
    if (currentViewAccess === 'Allowed Users' && option === 'All Users') {
      return "Edit Access cannot be set to 'All Users' when View Access is limited to specific users";
    }
    return undefined;
  };

  const renderAccessTypeSection = (accessLevel: 'view' | 'edit', title: string) => {
    const isView = accessLevel === 'view';
    const currentAccessType = isView ? accessConfig.viewAccessType : accessConfig.editAccessType;
    const currentAllowedUsers = isView ? accessConfig.viewAllowedUsers : accessConfig.editAllowedUsers;
    const errorKey = isView ? 'viewAccessType' : 'editAccessType';

    return (
      <div className="bg-white p-6 rounded-lg border border-gray-200">
        <FormField
          label={title}
          error={errors[errorKey]}
          required
        >
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {(['Owner Only', 'Allowed Users', 'All Users'] as AccessType[]).map((option) => (
                <RadioOptionWithTooltip
                  key={option}
                  option={option}
                  currentAccessType={currentAccessType}
                  isDisabled={!isView && isEditOptionDisabled(
                    option,
                    accessConfig.viewAccessType,
                    accessConfig.viewAllowedUsers
                  )}
                  accessLevel={accessLevel}
                  entityType={entityType}
                  disabledMessage={!isView ? getDisabledMessage(option, accessConfig.viewAccessType) : undefined}
                  onValueChange={(value) => handleAccessTypeChange(accessLevel, value as AccessType)}
                />
              ))}
            </div>

            {currentAccessType === 'Allowed Users' && (
              <div className="mt-4 bg-gray-50 p-4 rounded-lg border border-gray-200">
                                 {isLoadingUsers ? (
                  <div className="flex items-center gap-2">
                    <Spinner className="h-4 w-4" />
                    <span className="text-sm text-gray-500">Loading users...</span>
                  </div>
                ) : (
                  <>
                    <MultiSelect
                      options={users.map(user => ({
                        value: user.userId,
                        label: user.email
                      }))}
                      value={currentAllowedUsers}
                      onChange={(newValue: string[]) => {
                        handleAllowedUsersChange(accessLevel, newValue);
                      }}
                      placeholder="Select users..."
                      className="w-full"
                    />
                    {!isView && accessConfig.viewAccessType === 'Allowed Users' && (
                      <p className="text-sm text-gray-500 mt-2">
                        Note: Selected users will automatically get view access
                      </p>
                    )}
                  </>
                )}
                {users.length === 0 && !isLoadingUsers && (
                  <p className="text-sm text-gray-500 mt-1">No active users found</p>
                )}
              </div>
            )}
          </div>
        </FormField>
      </div>
    );
  };

  return (
    <CollapsibleSection
      title={`${entityType} Access Control`}
      description={`Configure who can access and modify this ${entityType.toLowerCase()}`}
      defaultOpen={true}
      icon={<ShieldCheck className="h-5 w-5" />}
    >
      <div className="space-y-6 mt-4">
        {renderAccessTypeSection('view', 'View Access')}
        {renderAccessTypeSection('edit', 'Edit Access')}
        {!isOwner && !isCurrentUserAdmin && hasEditAccess && (
          <p className="text-sm text-amber-600 mt-2">
            Note: You can add users but only owners and administrators can remove users or change access levels
          </p>
        )}
      </div>
    </CollapsibleSection>
  );
};
